import { Box, Divider, Flex, Text } from '@chakra-ui/react';
import { Button, FormFeedback, Input, useToast } from '@morressier/alembic';
import { Editor } from '@tinymce/tinymce-react';
import { FormProvider, useForm } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { ContactJournalFormValues } from '@publishing-engine/domain/models/contact-publisher';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactJournalSchema } from '@publishing-engine/domain/validation/contact-publisher';
import { contactJournal } from 'src/api/mutations';
import { FormFieldMultipleFileUpload } from '../FormField';

interface ContactJournalProps {
  submissionId: string;
  submissionContextId: string;
  manuscriptId: string;
  setIsContactModalOpen: (arg0: boolean) => void;
  reviewRequestId?: string;
}

const ContactJournal = ({
  submissionId,
  submissionContextId,
  manuscriptId,
  setIsContactModalOpen,
  reviewRequestId
}: ContactJournalProps) => {
  const methods = useForm<ContactJournalFormValues>({
    resolver: zodResolver(contactJournalSchema),
    mode: 'onChange',
    defaultValues: manuscriptId
      ? {
          subject: reviewRequestId
            ? `Issue or query for #${manuscriptId} review`
            : `Issue or query for #${manuscriptId} submission`,
          message: '',
          attachments: []
        }
      : {
          subject: '',
          message: '',
          attachments: []
        }
  });
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors }
  } = methods;

  const apiKeyTinymce = import.meta.env.VITE_TINY_MCE_CLIENT_KEY;
  const selfHostedTinymce = import.meta.env.VITE_SELF_HOSTED_TINY_MCE;

  const handleEditorChange = (content: string): void => {
    setValue('message', content, { shouldValidate: true, shouldDirty: true });
  };
  const toast = useToast();
  const isSubmitButtonEnabled =
    watch('message').length > 0 && watch('subject').length > 0;
  const ContactJournalMutation = contactJournal(
    submissionContextId!,
    submissionId
  );
  const isLoading = ContactJournalMutation.isPending;

  const onSubmit = async (values: ContactJournalFormValues) => {
    const sanitizedAttachments =
      values.attachments.length > 0
        ? values.attachments
            .filter(file => !file.errors?.length && file.url)
            .map(({ name, url }) => ({ name, url }))
        : [];

    const response = await ContactJournalMutation.mutateAsync({
      emailSubject: values.subject,
      emailMessage: values.message,
      emailAttachments: sanitizedAttachments,
      reviewRequestId: reviewRequestId
    });
    if (response.isSuccess) {
      toast({
        title: 'Your query has been submitted successfully.',
        variant: 'success'
      });
      setIsContactModalOpen(false);
    } else {
      toast({
        title: response.message.error,
        variant: 'error'
      });
    }
  };
  const allowedFileFormats = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx'
    ],
    'image/png': ['.png'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'video/mp4': ['.mp4'],
    'text/csv': ['.csv']
  };
  return (
    <Flex justifyContent={'center'}>
      <Box>
        <Flex bg={'white'} direction={'column'} p={5}>
          <Flex>
            <Text textStyle={'headingGroup'}>Contact Journal</Text>
          </Flex>
          <Divider />
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Flex mt={4} gap={3} direction={'column'}>
                <Flex direction={'column'} width={'100%'}>
                  <FieldLabel label="Subject" subText="" />
                  <Input {...register('subject')} disabled={true} />
                  {errors.subject && (
                    <FormFeedback variant="error">
                      {errors.subject.message}
                    </FormFeedback>
                  )}
                </Flex>
                <Flex direction={'column'} width={'100%'}>
                  <FieldLabel label="Message" subText="" />
                  <Box width="100%">
                    <Box
                      className={errors.message ? 'editorErrorContainer' : ''}
                      width="100%"
                    >
                      <Editor
                        {...(apiKeyTinymce
                          ? { apiKey: apiKeyTinymce }
                          : { tinymceScriptSrc: selfHostedTinymce })}
                        licenseKey={selfHostedTinymce ? 'gpl' : ''}
                        value={watch('message')}
                        init={{
                          menubar: false,
                          statusbar: false,
                          paste_as_text: true,
                          plugins: ['math', 'autoresize'],
                          toolbar:
                            'undo redo | formatselect | bold italic underline strikethrough | ' +
                            'alignleft aligncenter alignright alignjustify | ' +
                            'bullist numlist outdent indent | ' +
                            'link image table hr charmap | code preview | math',
                          min_height: 200,
                          entity_encoding: 'raw',
                          placeholder: 'Enter message here'
                        }}
                        onEditorChange={handleEditorChange}
                      />
                      <Text textStyle="textContext">Max 2500 characters</Text>
                    </Box>
                  </Box>
                  {errors.message && (
                    <FormFeedback variant="error">
                      {errors.message.message}
                    </FormFeedback>
                  )}
                </Flex>
                <Flex>
                  <FormFieldMultipleFileUpload
                    submissionContextId={submissionContextId}
                    submissionId={submissionId}
                    config={{
                      name: 'attachments',
                      params: {
                        validations: {
                          kind: 'fileArray',
                          maxSizeMB: 25,
                          maxItems: 15,
                          allowedFileFormats: allowedFileFormats,
                          required: false
                        },
                        labels: {
                          main: 'Attach File(s)',
                          subtitle: ''
                        }
                      },
                      caseDataFields: {
                        main: 'attachments'
                      }
                    }}
                    helperText="PDF, DOC, DOCX, PNG, JPG, JPEG, MP4, CSV. Executable Scripts are not allowed"
                  />
                </Flex>

                <Flex mt={4} justifyContent="flex-end" gap={3}>
                  <Button variant="secondary" onClick={() => reset()}>
                    Clear All
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    isLoading={isLoading}
                    disabled={!isSubmitButtonEnabled || isLoading}
                  >
                    Submit
                  </Button>
                </Flex>
              </Flex>
            </form>
          </FormProvider>
        </Flex>
      </Box>
    </Flex>
  );
};

export default ContactJournal;
